import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgStore } from '../store/org';
import { fmtDateTime } from '../api/velopulse';
import { OrgChip, DemoBanner, EnteringOverlay, VpState } from '../components/VelopulseShell';
import '../velopulse.css';

const AV_BG = [
  'linear-gradient(135deg,#3b82f6,#1d4ed8)',
  'linear-gradient(135deg,#10b981,#047857)',
  'linear-gradient(135deg,#8b5cf6,#6d28d9)',
  'linear-gradient(135deg,#f59e0b,#d97706)',
];
const bgOf = (id) => AV_BG[[...String(id)].reduce((a, c) => a + c.charCodeAt(0), 0) % AV_BG.length];

export default function OrgSwitchPage() {
  const navigate = useNavigate();
  const { orgs, orgsError, currentOrg, fetchOrgs, enterOrg, recentIds } = useOrgStore();
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState(currentOrg?.id || null);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const { recent, rest } = useMemo(() => {
    const list = (orgs || []).filter((o) => {
      const kw = q.trim().toLowerCase();
      return !kw || `${o.name} ${o.code || ''}`.toLowerCase().includes(kw);
    });
    const ids = recentIds();
    // 最近使用置頂：本機最近進入的順序優先，其次採後端回傳的 last_entered_at
    const isRecent = (o) => ids.includes(o.id) || !!o.last_entered_at;
    const rank = (o) => (ids.includes(o.id) ? ids.indexOf(o.id) : 99);
    return {
      recent: list.filter(isRecent).sort((a, b) => rank(a) - rank(b) || String(b.last_entered_at).localeCompare(String(a.last_entered_at))),
      rest: list.filter((o) => !isRecent(o)),
    };
  }, [orgs, q, recentIds]);

  useEffect(() => {
    if (!selectedId && recent.length) setSelectedId(recent[0].id);
  }, [selectedId, recent]);

  const selected = (orgs || []).find((o) => o.id === selectedId);

  const renderRow = (org) => (
    <button
      key={org.id}
      type="button"
      className={`vp-org-row${org.id === selectedId ? ' selected' : ''}`}
      aria-pressed={org.id === selectedId}
      onClick={() => setSelectedId(org.id)}
      onDoubleClick={() => enterOrg(org)}
    >
      <div className="vp-org-av" style={{ background: bgOf(org.id) }}>{org.name.slice(0, 1)}</div>
      <div className="vp-org-meta">
        <div className="n">{org.name}{org.id === currentOrg?.id && <span className="vp-muted" style={{ fontWeight: 400, fontSize: 12 }}>（目前）</span>}</div>
        <div className="d">
          角色：{org.role || '成員'}
          {org.last_entered_at && ` · 上次進入：${fmtDateTime(org.last_entered_at)}`}
        </div>
      </div>
      {org.industry && <span className="vp-tag-ind">{org.industry}</span>}
    </button>
  );

  return (
    <div className="vp-standalone">
      <DemoBanner />
      <div className="vp-top">
        <div className="vp-logo">Velo<span>pulse</span></div>
        <div className="vp-spacer" />
        <OrgChip />
      </div>
      <div className="vp-wrap">
        <h1>選擇要進入的組織</h1>
        <p className="vp-lead">切換後，工作台、紀錄與商機資料會完整更換。請確認您正在正確的公司脈絡中作業。</p>

        <div className="vp-org-list">
          <div className="vp-org-search">
            <input className="vp-input" type="search" placeholder="搜尋公司名稱或代碼…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="搜尋組織" />
          </div>
          {orgs === null && <VpState loading />}
          {orgs && orgs.length === 0 && (
            <VpState text={orgsError ? '目前無法取得組織清單，組織服務尚未啟用。請稍後再試或聯絡管理員。' : '您尚未加入任何組織，請聯絡管理員邀請您加入。'} />
          )}
          {orgs && orgs.length > 0 && !recent.length && !rest.length && <VpState text={`找不到符合「${q}」的組織。`} />}
          {recent.length > 0 && <div className="vp-org-sec">最近使用</div>}
          {recent.map(renderRow)}
          {rest.length > 0 && <div className="vp-org-sec">全部組織</div>}
          {rest.map(renderRow)}
        </div>

        <div className="vp-wrap-actions">
          <button type="button" className="vp-btn lg" onClick={() => navigate(-1)}>取消</button>
          <button type="button" className="vp-btn lg primary" disabled={!selected} onClick={() => enterOrg(selected)}>
            {selected ? `進入${selected.name}` : '請先選擇組織'}
          </button>
        </div>
      </div>
      <EnteringOverlay />
    </div>
  );
}
